import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  motion, useInView, useScroll, useTransform,
  useMotionValue, useSpring, AnimatePresence
} from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import OfferDeskNav from '../components/OfferDeskNav';
import JobRail from '../components/jobs/JobRail';
import { daysUntil, isNew, buildSkillFrequency, LOGO_TRADEMARK_NOTICE } from '../components/jobs/jobCardUtils';

/* ─── GradientDots ───────────────────────────────────────── */
function GradientDots({ dotSize = 8, spacing = 10, duration = 30, colorCycleDuration = 6, backgroundColor = '#fff', style = {}, ...props }) {
  const hexSpacing = spacing * 1.732;
  return (
    <motion.div
      style={{
        position: 'absolute', inset: 0, backgroundColor,
        backgroundImage: `
          radial-gradient(circle at 50% 50%, transparent 1.5px, ${backgroundColor} 0 ${dotSize}px, transparent ${dotSize}px),
          radial-gradient(circle at 50% 50%, transparent 1.5px, ${backgroundColor} 0 ${dotSize}px, transparent ${dotSize}px),
          radial-gradient(circle at 50% 50%, #4E6C7E, transparent 60%),
          radial-gradient(circle at 50% 50%, #6C969C, transparent 60%),
          radial-gradient(circle at 50% 50%, #6C969C, transparent 60%),
          radial-gradient(ellipse at 50% 50%, #3A5468, transparent 60%)
        `,
        backgroundSize: `${spacing}px ${hexSpacing}px, ${spacing}px ${hexSpacing}px, 200% 200%, 200% 200%, 200% 200%, 200% ${hexSpacing}px`,
        backgroundPosition: `0px 0px, ${spacing / 2}px ${hexSpacing / 2}px, 0% 0%, 0% 0%, 0% 0%, 0% 0px`,
        ...style,
      }}
      animate={{
        backgroundPosition: [
          `0px 0px, ${spacing / 2}px ${hexSpacing / 2}px, 800% 400%, 1000% -400%, -1200% -600%, 400% ${hexSpacing}px`,
          `0px 0px, ${spacing / 2}px ${hexSpacing / 2}px, 0% 0%, 0% 0%, 0% 0%, 0% 0%`,
        ],
        filter: ['hue-rotate(0deg)', 'hue-rotate(12deg)'],
      }}
      transition={{
        backgroundPosition: { duration, ease: 'linear', repeat: Infinity },
        filter: { duration: colorCycleDuration, ease: 'linear', repeat: Infinity },
      }}
      {...props}
    />
  );
}

/* ─── Floating Ambient Particles ─────────────────────────── */
function Particle({ x, size, duration, delay, opacity }) {
  return (
    <motion.div
      style={{
        position: 'absolute', bottom: -20, left: `${x}%`,
        width: size, height: size, borderRadius: '50%',
        background: `radial-gradient(circle, rgba(78,108,126,${opacity}), transparent 70%)`,
        pointerEvents: 'none',
      }}
      animate={{ y: [-20, -380 - Math.random() * 200], x: [0, (Math.random() - 0.5) * 80], opacity: [0, opacity * 1.5, 0], scale: [0.4, 1.2, 0.3] }}
      transition={{ duration, delay, repeat: Infinity, ease: 'easeOut' }}
    />
  );
}

function AmbientParticles({ count = 18 }) {
  const particles = useRef(
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      size: 4 + Math.random() * 14,
      duration: 4 + Math.random() * 5,
      delay: Math.random() * 6,
      opacity: 0.12 + Math.random() * 0.2,
    }))
  ).current;

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 2 }}>
      {particles.map(p => <Particle key={p.id} {...p} />)}
    </div>
  );
}

/* ─── Noise/Grain Overlay ────────────────────────────────── */
function GrainOverlay() {
  return (
    <div style={{
      position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999,
      opacity: 0.025,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'repeat',
      backgroundSize: '128px 128px',
      mixBlendMode: 'multiply',
    }} />
  );
}

/* ─── Magnetic Button ─────────────────────────────────────── */
function MagneticButton({ children, onClick, style = {}, strength = 0.35 }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 200, damping: 18 });
  const springY = useSpring(y, { stiffness: 200, damping: 18 });

  const handleMouseMove = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * strength);
    y.set((e.clientY - cy) * strength);
  }, [strength, x, y]);

  const handleMouseLeave = useCallback(() => {
    x.set(0); y.set(0);
  }, [x, y]);

  return (
    <motion.button
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      style={{ ...style, x: springX, y: springY, cursor: 'pointer', border: 'none' }}
      whileTap={{ scale: 0.95 }}
    >
      {children}
    </motion.button>
  );
}

/* ─── 3D Tilt Card ────────────────────────────────────────── */
function TiltCard({ children, style = {}, intensity = 8 }) {
  const ref = useRef(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const springRotateX = useSpring(rotateX, { stiffness: 180, damping: 20 });
  const springRotateY = useSpring(rotateY, { stiffness: 180, damping: 20 });

  const handleMouseMove = useCallback((e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    rotateY.set(((e.clientX - cx) / (rect.width / 2)) * intensity);
    rotateX.set(-((e.clientY - cy) / (rect.height / 2)) * intensity);
  }, [intensity, rotateX, rotateY]);

  const handleMouseLeave = useCallback(() => {
    rotateX.set(0); rotateY.set(0);
  }, [rotateX, rotateY]);

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        ...style,
        rotateX: springRotateX,
        rotateY: springRotateY,
        transformStyle: 'preserve-3d',
        transformPerspective: 900,
        willChange: 'transform',
      }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Scroll-triggered blur reveal ──────────────────────── */
function BlurReveal({ children, delay = 0, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28, filter: 'blur(10px)', scale: 0.98 }}
      animate={inView ? { opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── Simple FadeIn ──────────────────────────────────────── */
function FadeIn({ children, delay = 0, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── Icons ──────────────────────────────────────────────── */
const I = {
  briefcase: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  mail: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  target: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  star: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  search: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  fileText: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  barChart: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  users: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  zap: ({ s = 17 } = {}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  lock: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  arrowRight: ({ s = 13 } = {}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  lightbulb: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>,
  clock: ({ s = 17 } = {}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  pieChart: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>,
  trendingUp: () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
};

/* ─── Typewriter ─────────────────────────────────────────── */
const HEADLINES = ["Land your dream internship.", "Beat the application grind.", "Let AI do the heavy lifting.", "Your finance career starts here."];
function Typewriter() {
  const [idx, setIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const target = HEADLINES[idx];
    if (!deleting && displayed.length < target.length) { const t = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), 45); return () => clearTimeout(t); }
    if (!deleting && displayed.length === target.length) { const t = setTimeout(() => setDeleting(true), 2200); return () => clearTimeout(t); }
    if (deleting && displayed.length > 0) { const t = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 22); return () => clearTimeout(t); }
    if (deleting && displayed.length === 0) { setDeleting(false); setIdx((idx + 1) % HEADLINES.length); }
  }, [displayed, deleting, idx]);
  return <span>{displayed}<span style={{ animation: 'blink 1s step-end infinite', color: '#4E6C7E' }}>|</span></span>;
}

/* ─── Counter ────────────────────────────────────────────── */
function Counter({ to, suffix = '', duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0; const step = to / (duration / 16);
    const t = setInterval(() => { start += step; if (start >= to) { setCount(to); clearInterval(t); } else setCount(Math.floor(start)); }, 16);
    return () => clearInterval(t);
  }, [inView, to, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ─── Speed Counter ──────────────────────────────────────── */
function SpeedCounter() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let v = 0;
    const t = setInterval(() => { v += 0.15; if (v >= 8) { setS(8); clearInterval(t); } else setS(parseFloat(v.toFixed(1))); }, 16);
    return () => clearInterval(t);
  }, [inView]);
  return <span ref={ref} style={{ fontVariantNumeric: 'tabular-nums' }}>{s < 8 ? s.toFixed(1) : '8'}s</span>;
}

/* ─── Stat Card ──────────────────────────────────────────── */
function StatCard({ label, value, delay }) {
  return (
    <FadeIn delay={delay} style={{ height: '100%', minWidth: 0 }}>
      <TiltCard intensity={4} style={{
        background: '#fff', border: '1px solid var(--tandm-line)', borderRadius: 16,
        padding: '20px 22px', boxShadow: 'var(--tandm-shadow-soft)', cursor: 'default',
        position: 'relative', height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        minHeight: 108,
      }}>
        <div style={{
          fontSize: 28, fontWeight: 800, color: 'var(--tandm-ink)',
          letterSpacing: '-0.04em', lineHeight: 1, fontVariantNumeric: 'tabular-nums',
        }}>
          {value}
        </div>
        <div style={{
          fontSize: 12, color: 'var(--tandm-muted)', marginTop: 8, fontWeight: 500,
          lineHeight: 1.3, minHeight: '1.3em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {label}
        </div>
      </TiltCard>
    </FadeIn>
  );
}

/* ─── Action Card ────────────────────────────────────────── */
function ActionCard({ title, desc, onClick, accent, delay, badge }) {
  return (
    <BlurReveal delay={delay} style={{ height: '100%', minWidth: 0 }}>
      <TiltCard intensity={5} style={{
        background: '#fff', border: '1px solid var(--tandm-line)', borderRadius: 16,
        padding: '24px 22px', cursor: onClick ? 'pointer' : 'default',
        position: 'relative', boxShadow: 'var(--tandm-shadow-soft)',
        height: '100%', minHeight: 168,
        display: 'flex', flexDirection: 'column',
      }} onClick={onClick || undefined}>
        {badge && (
          <div style={{
            position: 'absolute', top: 14, right: 14, background: 'var(--tandm-accent-subtle)',
            color: 'var(--tandm-muted)', fontSize: 9, fontWeight: 700, padding: '3px 9px',
            borderRadius: 20, letterSpacing: '0.06em', textTransform: 'uppercase',
            border: '1px solid var(--tandm-line)',
          }}>
            {badge}
          </div>
        )}
        <div style={{
          fontSize: 15, fontWeight: 700, color: 'var(--tandm-ink)', marginBottom: 8,
          letterSpacing: '-0.02em', paddingRight: badge ? 88 : 0,
          lineHeight: 1.3, minHeight: '1.3em',
        }}>
          {title}
        </div>
        <div style={{
          fontSize: 13, color: 'var(--tandm-muted)', lineHeight: 1.55, flex: 1,
          minHeight: 60,
        }}>
          {desc}
        </div>
        <div style={{
          marginTop: 16, display: 'flex', alignItems: 'center', gap: 4,
          color: onClick ? (accent || 'var(--tandm-accent)') : 'var(--tandm-faint)',
          fontSize: 12, fontWeight: 600,
        }}>
          {onClick ? 'Get started' : 'Coming soon'}
          <I.arrowRight />
        </div>
      </TiltCard>
    </BlurReveal>
  );
}

/* ─── Rate My CV Card ────────────────────────────────────── */
function RateMyCVCard() {
  const [hovered, setHovered] = useState(false);

  return (
    <BlurReveal delay={0.05} style={{ height: '100%', minWidth: 0 }}>
      <TiltCard intensity={6} style={{
        background: 'linear-gradient(135deg, #0B1220 0%, #162033 100%)',
        borderRadius: 22, padding: '28px',
        position: 'relative', overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(11,18,32,0.18)',
        cursor: 'default',
        height: '100%',
      }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <GradientDots dotSize={5} spacing={10} duration={22} colorCycleDuration={7} backgroundColor="#0B1220" style={{ opacity: 0.4 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(11,18,32,0.75) 0%, rgba(11,18,32,0.55) 100%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Left: copy */}
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(124,172,183,0.9)', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Rate My CV</span>
              <motion.div
                animate={{ scale: hovered ? 1.06 : 1 }}
                style={{ background: 'linear-gradient(135deg, #4E6C7E, #6C969C)', color: '#fff', fontSize: 9, fontWeight: 800, padding: '4px 10px', borderRadius: 20, letterSpacing: '0.07em', textTransform: 'uppercase' }}
              >
                Coming Soon
              </motion.div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: 8 }}>ATS Compatibility Score</div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, margin: 0, maxWidth: 460 }}>
              Upload your CV and our AI will score it against real ATS systems used by top finance firms.
            </p>
          </div>

          {/* Right: gauge + checklist */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexShrink: 0 }}>
            <div style={{ position: 'relative', width: 88, height: 56, flexShrink: 0 }}>
              <svg width="88" height="56" viewBox="0 0 88 56">
                <path d="M 8 52 A 36 36 0 0 1 80 52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" strokeLinecap="round" />
                <motion.path
                  d="M 8 52 A 36 36 0 0 1 80 52"
                  fill="none" stroke="url(#dialGrad)" strokeWidth="8" strokeLinecap="round" strokeDasharray="113"
                  initial={{ strokeDashoffset: 113 }}
                  animate={{ strokeDashoffset: 113 * 0.38 }}
                  transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                />
                <defs>
                  <linearGradient id="dialGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#4E6C7E" />
                    <stop offset="100%" stopColor="#6C969C" />
                  </linearGradient>
                </defs>
                <text x="44" y="52" textAnchor="middle" style={{ fontSize: 14, fontWeight: 900, fill: '#fff', fontFamily: 'var(--tandm-font-sans)' }}>?</text>
              </svg>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 20px' }}>
              {['ATS Keywords', 'Formatting', 'Impact Phrases', 'Length & Structure'].map((item) => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 4, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <I.lock />
                  </div>
                  <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.32)', fontWeight: 500, whiteSpace: 'nowrap' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </TiltCard>
    </BlurReveal>
  );
}

/* ─── Rail skeleton (shown while internships load) ───────── */
function SkeletonRail({ title }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <h2 style={{ fontFamily: 'var(--tandm-font-display)', fontSize: 19, fontWeight: 500, color: 'var(--tandm-ink)', letterSpacing: '-0.02em', margin: 0 }}>{title}</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {[...Array(3)].map((_, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid var(--tandm-line)', borderRadius: 16, padding: 18, minHeight: 212 }}>
            <div className="dash-shimmer" style={{ height: 44, width: 44, borderRadius: 12, marginBottom: 14 }} />
            <div className="dash-shimmer" style={{ height: 10, width: '50%', borderRadius: 6, marginBottom: 8 }} />
            <div className="dash-shimmer" style={{ height: 14, width: '85%', borderRadius: 6, marginBottom: 16 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <div className="dash-shimmer" style={{ height: 22, width: 72, borderRadius: 8 }} />
              <div className="dash-shimmer" style={{ height: 22, width: 60, borderRadius: 8 }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Main Dashboard ─────────────────────────────────────── */
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, applied: 0, interviews: 0, offers: 0 });
  const [recentApps, setRecentApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [matchScores, setMatchScores] = useState({});
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingScores, setLoadingScores] = useState(true);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  useEffect(() => { fetchStats(); fetchJobs(); fetchMatchScores(); }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/applications');
      const apps = res.data.applications || [];
      setRecentApps(apps.slice(0, 4));
      setStats({
        total: apps.length,
        applied: apps.filter(a => a.status === 'applied').length,
        interviews: apps.filter(a => a.status === 'interview').length,
        offers: apps.filter(a => a.status === 'offer').length,
      });
    } catch {}
  };

  const fetchJobs = async () => {
    try {
      const res = await api.get('/internships');
      setJobs(res.data.internships || []);
    } catch {} finally { setLoadingJobs(false); }
  };
  const fetchMatchScores = async () => {
    try {
      const res = await api.get('/ai/match-scores');
      setMatchScores(res.data.scores || {});
    } catch {} finally { setLoadingScores(false); }
  };

  const scoreOf = (j) => matchScores[String(j.id)] ?? matchScores[j.id] ?? 0;
  const hasScores = Object.keys(matchScores).length > 0;
  const skillFreq = useMemo(() => buildSkillFrequency(jobs), [jobs]);
  const rails = useMemo(() => {
    const strong = [...jobs].filter((j) => scoreOf(j) >= 70).sort((a, b) => scoreOf(b) - scoreOf(a)).slice(0, 6);
    const closing = [...jobs].filter((j) => { const d = daysUntil(j.deadline); return d != null && d >= 0 && d <= 7; }).sort((a, b) => daysUntil(a.deadline) - daysUntil(b.deadline)).slice(0, 6);
    const fresh = [...jobs].filter((j) => isNew(j.created_at)).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6);
    return { strong, closing, fresh };
  }, [jobs, matchScores]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstName = user?.first_name || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6F7', fontFamily: "var(--tandm-font-sans)", overflowX: 'hidden' }}>
      <style>{`
        /* fonts via index.css tokens */
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes subtlePulse { 0%,100%{opacity:0.5} 50%{opacity:1} }
        @keyframes dashShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        .dash-shimmer { background: linear-gradient(90deg, var(--tandm-line-soft) 25%, var(--tandm-canvas-deep) 50%, var(--tandm-line-soft) 75%); background-size: 400px 100%; animation: dashShimmer 1.3s ease infinite; }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(78,108,126,0.35); border-radius: 3px; }
        @media (max-width: 900px) {
          .dash-stats-grid, .dash-actions-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .dash-tips-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 520px) {
          .dash-stats-grid, .dash-actions-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Global grain overlay ─────────────────────── */}
      <GrainOverlay />

      <OfferDeskNav />

      {/* ── HERO ─────────────────────────────────────── */}
      <motion.div ref={heroRef} style={{ y: heroY }}>
        <div style={{
          position: 'relative', overflow: 'hidden', minHeight: 430,
          paddingTop: 'var(--tandm-nav-clearance, 112px)',
          paddingLeft: 24, paddingRight: 24, paddingBottom: 68,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>

          <GradientDots dotSize={7} spacing={14} duration={28} colorCycleDuration={8} backgroundColor="#fff" />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.52) 60%, rgba(255,255,255,0.82) 100%)', backdropFilter: 'blur(0.4px)', pointerEvents: 'none', zIndex: 1 }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 90, background: 'linear-gradient(to bottom, transparent, #F4F6F7)', pointerEvents: 'none', zIndex: 3 }} />

          {/* Floating particles */}
          <AmbientParticles count={20} />

          <div style={{ position: 'relative', zIndex: 4, maxWidth: 700, width: '100%', textAlign: 'center' }}>
            <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(255,255,255,0.88)', border: '1px solid rgba(78,108,126,0.22)', borderRadius: 100, padding: '6px 16px', marginBottom: 22, backdropFilter: 'blur(8px)', boxShadow: '0 2px 12px rgba(78,108,126,0.1)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4E6C7E', display: 'inline-block', animation: 'subtlePulse 2s ease infinite' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#4E6C7E', letterSpacing: '0.04em' }}>{greeting}, {firstName}</span>
              </div>

              <h1 style={{ fontFamily: 'var(--tandm-font-display)', fontSize: 'clamp(30px, 4.8vw, 48px)', fontWeight: 600, color: '#0B1220', letterSpacing: '-0.03em', lineHeight: 1.12, margin: '0 0 18px', minHeight: '1.2em' }}>
                <Typewriter />
              </h1>

              <p style={{ fontSize: 15, color: '#475569', maxWidth: 440, margin: '0 auto 34px', lineHeight: 1.65, fontWeight: 400 }}>
                AI-powered applications, match scores, and cover letters — built for finance students.
              </p>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <MagneticButton
                  onClick={() => navigate('/jobs')}
                  style={{ background: '#4E6C7E', color: '#fff', borderRadius: 13, padding: '13px 26px', fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', boxShadow: '0 4px 18px rgba(78,108,126,0.32)', display: 'flex', alignItems: 'center', gap: 7 }}
                >
                  Browse Internships <I.arrowRight />
                </MagneticButton>
                <MagneticButton
                  onClick={() => navigate('/profile')}
                  style={{ background: 'rgba(255,255,255,0.88)', color: '#475569', border: '1.5px solid rgba(78,108,126,0.18)', borderRadius: 13, padding: '13px 26px', fontSize: 14, fontWeight: 600, backdropFilter: 'blur(8px)' }}
                >
                  Complete Profile
                </MagneticButton>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px 88px' }}>

        {/* ── STATS ─────────────────────────────────── */}
        <div style={{ marginTop: 8, marginBottom: 36 }}>
          <div className="dash-stats-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 12,
            alignItems: 'stretch',
          }}>
            <StatCard label="Total Applications" value={<Counter to={stats.total} />} delay={0} />
            <StatCard label="Applied" value={<Counter to={stats.applied} />} delay={0.07} />
            <StatCard label="Interviews" value={<Counter to={stats.interviews} />} delay={0.14} />
            <StatCard label="Offers" value={<Counter to={stats.offers} />} delay={0.21} />
          </div>
        </div>

        {/* ── QUICK ACTIONS ─────────────────────────── */}
        <FadeIn><h2 style={{ fontSize: 19, fontWeight: 800, color: '#0B1220', letterSpacing: '-0.03em', margin: '4px 0 16px' }}>Quick Actions</h2></FadeIn>

        <div className="dash-actions-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 14,
          marginBottom: 52,
          alignItems: 'stretch',
        }}>
          <ActionCard title="Browse Internships" accent="#4E6C7E" delay={0} desc="Explore curated roles matched to your profile and interests." onClick={() => navigate('/jobs')} />
          <ActionCard title="AI Cover Letters" accent="#4E6C7E" delay={0.07} desc="Generate personalised cover letters in seconds with AI." onClick={() => navigate('/jobs')} />
          <ActionCard title="Track Applications" accent="#4E6C7E" delay={0.14} desc="Manage your pipeline from saved to offer in one place." onClick={() => navigate('/applications')} />
          <ActionCard title="Reach Out" accent="#6C969C" delay={0.21} badge="Coming Soon" desc="Connect with professionals at your target firms." onClick={null} />
        </div>

        {/* ── FEATURE BENTO ROW 1 ───────────────────── */}
        <FadeIn><h2 style={{ fontSize: 19, fontWeight: 800, color: '#0B1220', letterSpacing: '-0.03em', margin: '0 0 16px' }}>What Tandm Does For You</h2></FadeIn>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14, marginBottom: 14 }}>

          {/* Match Scoring */}
          <BlurReveal delay={0} style={{ height: '100%' }}>
            <TiltCard intensity={6} style={{ background: '#fff', border: '1px solid #D9E2E6', borderRadius: 24, padding: '32px', boxShadow: '0 2px 14px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden', height: '100%' }}>
              <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(78,108,126,0.1), transparent 70%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#4E6C7E', letterSpacing: '0.07em', textTransform: 'uppercase' }}>AI Match Scoring</span>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0B1220', letterSpacing: '-0.03em', marginBottom: 10 }}>Know before you apply</div>
                  <p style={{ fontSize: 12.5, color: '#94A3B8', lineHeight: 1.65, margin: '0 0 16px' }}>Our AI reads your CV and scores every internship on fit — focus energy where it counts.</p>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['CV-aware', 'Role-specific', 'Instant'].map(tag => (
                      <span key={tag} style={{ fontSize: 10.5, fontWeight: 600, color: '#4E6C7E', background: '#E8F1F3', border: '1px solid #D9E2E6', borderRadius: 20, padding: '3px 10px' }}>{tag}</span>
                    ))}
                  </div>
                </div>
                <div style={{ flexShrink: 0, position: 'relative' }}>
                  <svg width="104" height="104" viewBox="0 0 104 104">
                    <circle cx="52" cy="52" r="42" fill="none" stroke="#EEF2F4" strokeWidth="11" />
                    <circle cx="52" cy="52" r="42" fill="none" stroke="url(#og)" strokeWidth="11"
                      strokeDasharray={`${2 * Math.PI * 42 * 0.73} ${2 * Math.PI * 42 * 0.27}`}
                      strokeLinecap="round" strokeDashoffset={2 * Math.PI * 42 * 0.25} />
                    <defs><linearGradient id="og" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4E6C7E" /><stop offset="100%" stopColor="#6C969C" /></linearGradient></defs>
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 22, fontWeight: 900, color: '#0B1220', letterSpacing: '-0.03em' }}>73%</span>
                    <span style={{ fontSize: 9.5, color: '#94A3B8', fontWeight: 500 }}>match</span>
                  </div>
                </div>
              </div>
            </TiltCard>
          </BlurReveal>

          {/* Cover Letters */}
          <BlurReveal delay={0.1} style={{ height: '100%' }}>
            <TiltCard intensity={6} style={{ background: '#0B1220', borderRadius: 24, padding: '32px', position: 'relative', overflow: 'hidden', boxShadow: '0 2px 14px rgba(11,18,32,0.14)', height: '100%' }}>
              <GradientDots dotSize={5} spacing={10} duration={22} colorCycleDuration={7} backgroundColor="#0B1220" style={{ opacity: 0.5 }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(11,18,32,0.7) 0%, rgba(11,18,32,0.5) 100%)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ marginBottom: 14 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(124,172,183,0.95)', letterSpacing: '0.07em', textTransform: 'uppercase' }}>AI Cover Letters</span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: 8 }}>Personalised, in seconds</div>
                <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, margin: '0 0 22px' }}>CV-aware, firm-specific letters that sound like you wrote them.</p>
                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 14, padding: '18px 20px', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Average generation time</span>
                  </div>
                  <div style={{ fontSize: 44, fontWeight: 900, color: '#fff', letterSpacing: '-0.04em', lineHeight: 1 }}><SpeedCounter /></div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 5, fontWeight: 500 }}>per cover letter</div>
                </div>
              </div>
            </TiltCard>
          </BlurReveal>
        </div>

        {/* ── RATE MY CV (full-width banner) ─────────── */}
        <div style={{ marginBottom: 52 }}>
          <RateMyCVCard />
        </div>

        {/* ── RECENT APPLICATIONS ───────────────────── */}
        {recentApps.length > 0 && (
          <>
            <FadeIn>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ fontSize: 19, fontWeight: 800, color: '#0B1220', letterSpacing: '-0.03em', margin: 0 }}>Recent Activity</h2>
                <MagneticButton
                  onClick={() => navigate('/applications')}
                  style={{ background: '#fff', border: '1.5px solid #D9E2E6', borderRadius: 10, padding: '7px 15px', fontSize: 12.5, fontWeight: 600, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 5 }}
                  strength={0.25}
                >
                  View all <I.arrowRight />
                </MagneticButton>
              </div>
            </FadeIn>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 52 }}>
              {recentApps.map((app, i) => {
                const sc = { saved: '#94A3B8', applied: '#6366F1', interview: '#10B981', offer: '#6C969C', rejected: '#EF4444' }[app.status] || '#94A3B8';
                return (
                  <BlurReveal key={app.id} delay={i * 0.06}>
                    <motion.div whileHover={{ x: 4, boxShadow: '0 4px 18px rgba(0,0,0,0.08)' }}
                      style={{ background: '#fff', border: '1px solid #D9E2E6', borderRadius: 15, padding: '15px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 11, background: '#E8F1F3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#4E6C7E', flexShrink: 0, border: '1px solid #D9E2E6' }}>
                        {app.company_name?.charAt(0)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0B1220', marginBottom: 2, letterSpacing: '-0.01em' }}>{app.title}</div>
                        <div style={{ fontSize: 12, color: '#94A3B8' }}>{app.company_name}</div>
                      </div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: sc, background: `${sc}12`, borderRadius: 20, padding: '4px 12px', textTransform: 'capitalize', letterSpacing: '0.02em', border: `1px solid ${sc}20`, flexShrink: 0 }}>{app.status}</div>
                    </motion.div>
                  </BlurReveal>
                );
              })}
            </div>
          </>
        )}

        {/* ── EDITORIAL RAILS ──────────────────────── */}
        {loadingJobs ? (
          <div>
            <SkeletonRail title="Strong matches for you" />
            <SkeletonRail title="Closing this week" />
            <SkeletonRail title="New this week" />
          </div>
        ) : (rails.strong.length > 0 || rails.closing.length > 0 || rails.fresh.length > 0 || loadingScores) ? (
          <div>
            {loadingScores
              ? <SkeletonRail title="Strong matches for you" />
              : (hasScores && rails.strong.length > 0)
                ? <JobRail grid title="Strong matches for you" hint="Based on your CV" jobs={rails.strong} matchScores={matchScores} skillFreq={skillFreq} />
                : null}
            <JobRail grid title="Closing this week" hint="Apply before the deadline" jobs={rails.closing} matchScores={matchScores} skillFreq={skillFreq} />
            <JobRail grid title="New this week" jobs={rails.fresh} matchScores={matchScores} skillFreq={skillFreq} />

            <p style={{ fontSize: 11, color: 'var(--tandm-faint)', lineHeight: 1.6, textAlign: 'center', maxWidth: 680, margin: '32px auto 0' }}>
              {LOGO_TRADEMARK_NOTICE}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
